import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Button, IconButton, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText, Chip, Skeleton, Typography
} from '@mui/material';
import {
  GridColDef, GridValueGetterParams
} from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CheckIcon from '@mui/icons-material/Check';
import CrossIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import { fetchCostCategories, deleteCostCategory } from '../../store/costCategoryStore';
import DialogCostCategoryForm from './DialogCostCategoryForm';
import { ICostCategory } from '../../utils/costCategory.types';
import CustomDataGrid from '../DataGrid/DataGrid';

interface CostCategoryDataGridProps {
  openFormExternal?: boolean;
  onCloseFormExternal?: () => void;
}

const CostCategoryDataGrid: FC<CostCategoryDataGridProps> = ({ openFormExternal, onCloseFormExternal }) => {
  const dispatch = useAppDispatch();
  const { categories, status } = useAppSelector(state => state.costCategories);
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [editData, setEditData] = useState<ICostCategory | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<ICostCategory | null>(null);

  useEffect(() => {
    if (selectedAccount?._id) {
      dispatch(fetchCostCategories(true));
    }
  }, [dispatch, selectedAccount]);

  // Handle external form open
  useEffect(() => {
    if (openFormExternal) {
      setEditData(null);
      setFormOpen(true);
    }
  }, [openFormExternal]);

  const handleCloseForm = () => {
    setEditData(null);
    setFormOpen(false);
    if (onCloseFormExternal) {
      onCloseFormExternal();
    }
  };

  const handleEdit = useCallback((category: ICostCategory) => {
    setEditData(category);
    setFormOpen(true);
  }, []);

  const handleDeleteClick = useCallback((category: ICostCategory) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (categoryToDelete) {
      try {
        await dispatch(deleteCostCategory(categoryToDelete._id)).unwrap();
        dispatch(fetchCostCategories(true));
      } catch (error) {
        console.error('Failed to delete cost category:', error);
      }
    }
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleAddCategory = () => {
    setEditData(null);
    setFormOpen(true);
  };

  // Status check similar to Employee List
  const checkStatus = useCallback((value: boolean) => {
    return value ?
      <Chip label={<CheckIcon style={{ color: '#49a563', fontSize: 22, marginLeft: -5, marginTop: 5 }} />}
            style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '1px solid #49a563', borderRadius: 25, width: 35, height: 35 }} />
      :
      <Chip label={<CrossIcon style={{ color: '#c33b3b', fontSize: 18, marginLeft: -9, marginTop: 7 }} />}
            style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '1px solid #c33b3b', borderRadius: 25, width: 25, height: 25 }} />;
  }, []);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Category Name',
      type: "string",
      width: 200,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <span style={{ color: '#333' }}>{value}</span>
      )
    },
    {
      field: 'alias',
      headerName: 'Alias',
      type: "string",
      width: 120,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        value ? (
          <Chip
            label={value}
            size="small"
            style={{
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              fontSize: '12px',
              fontWeight: 500
            }}
          />
        ) : (
          <span style={{ color: '#9da7b7' }}>-</span>
        )
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      type: "string",
      flex: 1,
      minWidth: 300,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <span style={{ color: value ? '#333' : '#9da7b7' }}>
          {value || 'No description'}
        </span>
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      type: "string",
      width: 100,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <>{checkStatus(value)}</>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: "string",
      width: 120,
      sortable: false,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <>
          <IconButton
            style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
            size='small'
            onClick={() => handleEdit(row)}
          >
            <EditIcon style={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '0.1px solid #c33b3b', height: 35, width: 35 }}
            size='small'
            onClick={() => handleDeleteClick(row)}
          >
            <DeleteIcon style={{ fontSize: 16 }} />
          </IconButton>
        </>
      )
    }
  ], [checkStatus, handleEdit, handleDeleteClick]);

  return (
    <>

      <div style={{ height: "77vh", width: "100%", marginTop: '10px', overflow: 'auto' }}>
        {status === ApiStatus.LOADING ? (
          <Box sx={{ p: 4 }}>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} />
          </Box>
        ) : (categories && categories.length > 0) ? (
          <CustomDataGrid
            columns={columns}
            rows={categories}
            checkboxSelection={false}
            disableColumnMenu={true}
            disableSelectionOnClick={true}
            getRowId={(row) => row?._id || Math.random().toString()}
          />
        ) : (
          <Paper
            elevation={0}
            sx={{
              height: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fafafb',
              border: '2px dashed #e0e0e0',
              borderRadius: 2,
              p: 4
            }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
              {/* Icon */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 3
                }}
              >
                <CategoryIcon sx={{ fontSize: 60, color: 'primary.main' }} />
              </Box>

              {/* Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  mb: 1
                }}
              >
                No Cost Categories Yet
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Create categories to organize your cost centres effectively.
                Examples: Branches, Departments, Projects, etc.
              </Typography>

              {/* CTA Button */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleAddCategory}
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: (theme) => `0 8px 25px -8px ${theme.palette.primary.main}`,
                    '&::before': {
                      transform: 'translateX(0)',
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s ease',
                    zIndex: 1
                  },
                  '&:active': {
                    transform: 'translateY(-2px) scale(0.98)',
                  },
                  '& .MuiButton-startIcon': {
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover .MuiButton-startIcon': {
                    transform: 'rotate(360deg)',
                  }
                }}
              >
                Add Your First Category
              </Button>

              {/* Additional Help Text */}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3,
                  color: '#999'
                }}
              >
                Categories help you group and manage cost centres efficiently
              </Typography>
            </Box>
          </Paper>
        )}
      </div>

      <DialogCostCategoryForm
        open={formOpen}
        onClose={handleCloseForm}
        editData={editData}
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category "{categoryToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CostCategoryDataGrid;
